// Searchable multi-select for limiting a member (or invite) to specific groups and
// screens (S7 team-scopes). Shared by MemberScopeDrawer (editing an existing
// member) and the invite form's "Limit access" collapsible, so the picking
// experience is identical in both places.

import { useState } from 'react';
import { useGroups } from '../../api/groups.ts';
import { useScreens } from '../../api/screens.ts';
import { Loading } from '../../components/ui.tsx';
import { selectedFirst } from './scopeUtils.ts';

function PickerSection({
  label,
  items,
  selected,
  onToggle,
  emptyText,
}: {
  label: string;
  items: { id: string; name: string }[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  emptyText: string;
}) {
  const [q, setQ] = useState('');
  const filtered = selectedFirst(items, selected).filter((i) =>
    i.name.toLowerCase().includes(q.trim().toLowerCase()),
  );

  return (
    <div className="scope-picker-section">
      <div className="scope-picker-head">
        <span className="card-title" style={{ marginBottom: 0 }}>
          {label}
        </span>
        <span className="text-dim" style={{ fontSize: 'var(--fs-xs)' }}>
          {selected.size} selected
        </span>
      </div>
      <input
        type="search"
        className="search"
        aria-label={`Search ${label.toLowerCase()}`}
        placeholder={`Search ${label.toLowerCase()}…`}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {items.length === 0 ? (
        <p className="text-dim mt-2">{emptyText}</p>
      ) : (
        <div className="pl-multipick scope-picker-list">
          {filtered.map((i) => {
            const on = selected.has(i.id);
            return (
              <label key={i.id} className={`pl-multipick-row${on ? ' selected' : ''}`}>
                <input type="checkbox" checked={on} onChange={() => onToggle(i.id)} />
                <span className="pl-multipick-name">{i.name}</span>
              </label>
            );
          })}
          {filtered.length === 0 && <p className="text-dim mt-2">No matches.</p>}
        </div>
      )}
    </div>
  );
}

export function ScopePicker({
  selectedScreens,
  selectedGroups,
  onToggleScreen,
  onToggleGroup,
}: {
  selectedScreens: Set<string>;
  selectedGroups: Set<string>;
  onToggleScreen: (id: string) => void;
  onToggleGroup: (id: string) => void;
}) {
  const groups = useGroups();
  const screens = useScreens();

  if (groups.isLoading || screens.isLoading) return <Loading />;

  return (
    <div className="scope-picker">
      <PickerSection
        label="Groups"
        items={(groups.data ?? []).map((g) => ({ id: g.id, name: g.name }))}
        selected={selectedGroups}
        onToggle={onToggleGroup}
        emptyText="No groups yet."
      />
      <PickerSection
        label="Screens"
        items={(screens.data ?? []).map((s) => ({ id: s.id, name: s.name }))}
        selected={selectedScreens}
        onToggle={onToggleScreen}
        emptyText="No screens yet."
      />
    </div>
  );
}

/** Name lookup maps for the optimistic update — built from the same queries the
 *  picker uses, so the caller doesn't need a second round-trip. */
export function useScopeNameMaps(): { screenNames: Map<string, string>; groupNames: Map<string, string> } {
  const groups = useGroups();
  const screens = useScreens();
  return {
    screenNames: new Map((screens.data ?? []).map((s) => [s.id, s.name])),
    groupNames: new Map((groups.data ?? []).map((g) => [g.id, g.name])),
  };
}
